import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Modal, Form, Input, message, Popconfirm, Typography, Layout, Tag, Tooltip } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import api from '../../api';

const { Title } = Typography;
const { Content } = Layout;

const Customers = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [search, setSearch] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();

  const fetchCustomers = useCallback(async (page = 1, pageSize = 10, searchTerm = '') => {
    setLoading(true);
    try {
      const response = await api.get('/v1/customers', {
        params: {
          page: page,
          pageSize: pageSize,
          search: searchTerm,
        },
      });
      setData(response.data.data.map(item => ({ ...item, key: item.id })));
      setPagination({
        current: page,
        pageSize: pageSize,
        total: response.data.total,
      });
    } catch (error) {
      message.error('获取客户列表失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const handleTableChange = (newPagination) => {
    fetchCustomers(newPagination.current, newPagination.pageSize, search);
  };

  const handleSearch = (value) => {
    setSearch(value);
    fetchCustomers(1, pagination.pageSize, value);
  };

  const showModal = (record = null) => {
    setEditingRecord(record);
    if (record) {
      form.setFieldsValue(record);
    } else {
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingRecord(null);
    form.resetFields();
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if (editingRecord) {
        await api.put(`/v1/customers/${editingRecord.id}`, values);
        message.success('客户更新成功');
      } else {
        await api.post('/v1/customers', values);
        message.success('客户创建成功');
      }
      handleCancel();
      fetchCustomers(pagination.current, pagination.pageSize);
    } catch (error) {
      const errorMsg = error.response?.data?.error || (editingRecord ? '更新失败' : '创建失败');
      message.error(errorMsg);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/v1/customers/${id}`);
      message.success('客户删除成功');
      fetchCustomers(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error('删除失败');
    }
  };

  const [orderModalVisible, setOrderModalVisible] = useState(false);

  const fetchCustomerOrders = async (customerId) => {
    setModalLoading(true);
    try {
      const res = await api.get('/v1/sales_orders', {
        params: { pageSize: -1, customer_id: customerId }
      });
      setModalOrders(res.data.data || []);
    } catch (error) {
      message.error('获取订单失败');
    } finally {
      setModalLoading(false);
    }
  };
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [orderFilterType, setOrderFilterType] = useState('all');
  const [modalOrders, setModalOrders] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);

  const columns = [
    { title: '公司名称', dataIndex: 'company_name', key: 'company_name' },
    { title: '联系人', dataIndex: 'contact_person', key: 'contact_person' },
    { title: '联系电话', dataIndex: 'phone', key: 'phone' },
    { 
      title: '订单数', 
      dataIndex: 'order_count', 
      key: 'order_count', 
      render: (count, record) => (
        <Button 
          type="link" 
          size="small" 
          style={{ padding: 0, textDecoration: 'underline' }}
          onClick={() => {
            setSelectedCustomer(record);
            setOrderFilterType('all');
            setOrderModalVisible(true);
            fetchCustomerOrders(record.id);
          }}
        >
          {count || 0}
        </Button>
      )
    },
    { 
      title: '订单总额', 
      dataIndex: 'total_order_amount', 
      key: 'total_order_amount', 
      width: 120,
      render: (val) => `¥${(val || 0).toLocaleString()}` 
    },
    { 
      title: '已付金额', 
      dataIndex: 'total_paid_amount', 
      key: 'total_paid_amount', 
      width: 120,
      render: (val) => <span style={{ color: '#52c41a' }}>¥{(val || 0).toLocaleString()}</span> 
    },
    { 
      title: '待付金额', 
      dataIndex: 'total_pending_amount', 
      key: 'total_pending_amount', 
      width: 120,
      render: (val, record) => (
        <Button 
          type="link" 
          size="small" 
          danger
          style={{ padding: 0, textDecoration: 'underline' }}
          onClick={() => {
            setSelectedCustomer(record);
            setOrderFilterType('pending');
            setOrderModalVisible(true);
            fetchCustomerOrders(record.id);
          }}
        >
          ¥{(val || 0).toLocaleString()}
        </Button>
      )
    },
    { 
      title: '备注', 
      dataIndex: 'note', 
      key: 'note',
      ellipsis: true,
      width: 300,
      render: (note) => (
        <Tooltip title={note || '无备注'} placement="topLeft">
          <span>{note || '-'}</span>
        </Tooltip>
      )
    },
    {
      title: '操作',
      key: 'action',
      align: 'left',
      render: (_, record) => (
        <Space size="small" style={{ marginLeft: -8 }}>
          <Button type="link" size="small" onClick={() => showModal(record)}>编辑</Button>
          <Popconfirm
            title="确定删除此客户吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Content>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>客户管理</Title>
        <Space>
          <Input.Search
            placeholder="搜索公司名称、联系人或电话"
            onSearch={handleSearch}
            style={{ width: 250 }}
            allowClear
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>
            新增客户
          </Button>
        </Space>
      </div>
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        bordered
        size="small"
      />

      <Modal
        title={
          <div>
            <div style={{ fontSize: '16px', fontWeight: '600' }}>
              {`${orderFilterType === 'pending' ? '待付款' : '全部'}订单列表: ${selectedCustomer?.company_name || ''}`}
            </div>
            <div style={{ fontSize: '12px', color: '#8c8c8c', fontWeight: 'normal', marginTop: '4px' }}>
              <Space size="middle">
                <span>联系人：{selectedCustomer?.contact_person || '无'}</span>
                <span>联系电话：{selectedCustomer?.phone || '无'}</span>
              </Space>
            </div>
          </div>
        }
        open={orderModalVisible}
        onCancel={() => setOrderModalVisible(false)}
        footer={null}
        width={1100}
      >
        <Table
          size="small"
          rowKey="id"
          loading={modalLoading}
          dataSource={modalOrders.filter(order => 
            orderFilterType === 'all' || (Number(order.paid_amount || 0) < Number(order.total_amount || 0))
          )}
          pagination={false}
          columns={[
            { title: '订单号', dataIndex: 'order_no', key: 'order_no', width: 160 },
            { title: '日期', dataIndex: 'order_date', key: 'order_date', width: 120 },
            { title: '总金额', dataIndex: 'total_amount', key: 'total_amount', width: 100, render: (v) => `¥${v}` },
            { title: '已付', dataIndex: 'paid_amount', key: 'paid_amount', width: 100, render: (v) => `¥${v}` },
            { 
              title: '状态', 
              dataIndex: 'status', 
              key: 'status', 
              width: 100,
              render: (status) => {
                const statusMap = {
                  'pending': <Tag color="orange">待处理</Tag>,
                  'completed': <Tag color="green">已完成</Tag>,
                  'cancelled': <Tag color="default">已取消</Tag>
                };
                return statusMap[status] || <Tag>{status}</Tag>;
              }
            },
            { title: '发货地址', dataIndex: 'delivery_address', key: 'delivery_address', width: 250 },
            { title: '备注', dataIndex: 'note', key: 'note' }
          ]}
          expandedRowRender={(order) => (
            <div style={{ padding: '8px 24px', background: '#fafafa' }}>
              <Table
                size="small"
                pagination={false}
                columns={[
                  { title: '产品名称', dataIndex: ['product', 'name'], key: 'product_name' },
                  { title: '规格', dataIndex: ['product', 'spec'], key: 'product_spec' },
                  { title: '数量', dataIndex: 'quantity', key: 'quantity' },
                  { title: '单价', dataIndex: 'unit_price', key: 'unit_price', render: (v) => `¥${v}` },
                  { title: '小计', dataIndex: 'amount', key: 'amount', render: (v) => `¥${v}` }
                ]}
                dataSource={order.sales_items || []}
                rowKey="id"
              />
            </div>
          )}
        />
      </Modal>
      <Modal
        title={editingRecord ? '编辑客户' : '新增客户'}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        destroyOnClose
      >
        <Form form={form} layout="vertical" name="customer_form">
          <Form.Item name="company_name" label="公司名称" rules={[{ required: true, message: '请输入公司名称!' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="contact_person" label="联系人">
            <Input />
          </Form.Item>
          <Form.Item name="phone" label="联系电话">
            <Input />
          </Form.Item>
          <Form.Item name="email" label="邮箱">
            <Input type="email" />
          </Form.Item>
          <Form.Item name="address" label="联系地址">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="note" label="备注">
            <Input.TextArea rows={2} placeholder="请输入客户备注信息" />
          </Form.Item>
        </Form>
      </Modal>
    </Content>
  );
};

export default Customers;
